import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { 
    Mic, 
    MicOff, 
    Play,
    Square,
    Volume2,
    VolumeX,
    Settings,
    GraduationCap,
    AlertCircle,
    CheckCircle2,
    XCircle,
    Loader2,
    BookOpen,
    ArrowRight,
    Sparkles,
    Send,
    MessageSquare,
    Eye,
    EyeOff
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper component to render markdown content cleanly
const MarkdownText = ({ children }) => (
    <ReactMarkdown
        components={{
            p: ({children}) => <span>{children}</span>,
            strong: ({children}) => <strong className="font-semibold">{children}</strong>,
            em: ({children}) => <em className="italic">{children}</em>,
            h1: ({children}) => <span className="font-semibold">{children}</span>,
            h2: ({children}) => <span className="font-semibold">{children}</span>,
            h3: ({children}) => <span className="font-semibold">{children}</span>,
            ul: ({children}) => <span>{children}</span>,
            ol: ({children}) => <span>{children}</span>,
            li: ({children}) => <span>{children} </span>,
        }}
    >
        {children || ''}
    </ReactMarkdown>
);

// Common Surahs for practice
const SURAHS = [
    { value: 'al-fatiha', label: 'Al-Fatiha (The Opening)', ayat: 7, number: 1 },
    { value: 'al-ikhlas', label: 'Al-Ikhlas (Sincerity)', ayat: 4, number: 112 },
    { value: 'al-falaq', label: 'Al-Falaq (The Daybreak)', ayat: 5, number: 113 },
    { value: 'an-nas', label: 'An-Nas (Mankind)', ayat: 6, number: 114 },
    { value: 'al-kawthar', label: 'Al-Kawthar (Abundance)', ayat: 3, number: 108 },
    { value: 'al-asr', label: 'Al-Asr (Time)', ayat: 3, number: 103 },
    { value: 'al-mulk', label: 'Al-Mulk (Sovereignty)', ayat: 30, number: 67 },
    { value: 'ya-sin', label: 'Ya-Sin', ayat: 83, number: 36 },
    { value: 'ar-rahman', label: 'Ar-Rahman (The Merciful)', ayat: 78, number: 55 },
    { value: 'al-baqarah', label: 'Al-Baqarah (The Cow)', ayat: 286, number: 2 },
];

// Reference Reciter Audio Button Component
const ReferenceReciterButton = ({ name, style, description, surah, reciterId }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const audioRef = useRef(null);
    
    const surahData = SURAHS.find(s => s.value === surah);
    const surahNumber = surahData?.number || 1;
    
    // Use islamic.network CDN for full surah audio (proven working)
    // Format: https://cdn.islamic.network/quran/audio/{bitrate}/{reciterId}/{surahNumber}.mp3
    const audioUrl = `https://cdn.islamic.network/quran/audio/128/${reciterId}/${surahNumber}.mp3`;
    
    const handlePlayPause = async () => {
        if (!audioRef.current) {
            audioRef.current = new Audio(audioUrl);
            audioRef.current.onended = () => setIsPlaying(false);
            audioRef.current.onerror = () => {
                toast.error('Unable to load recitation', {
                    description: 'This recitation may not be available for the selected Surah.'
                });
                setIsPlaying(false);
                setIsLoading(false);
            };
        }
        
        if (isPlaying) {
            audioRef.current.pause();
            setIsPlaying(false);
        } else {
            setIsLoading(true);
            try {
                await audioRef.current.play();
                setIsPlaying(true);
            } catch (error) {
                toast.error('Playback failed', { description: error.message });
            } finally {
                setIsLoading(false);
            }
        }
    };
    
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);
    
    // Reset audio when surah changes
    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
            setIsPlaying(false);
        }
    }, [surah]);
    
    return (
        <div className="p-3 rounded-lg border border-border hover:border-secondary/50 transition-colors">
            <div className="flex items-center justify-between mb-2">
                <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{name}</p>
                    <p className="text-xs text-secondary">{style}</p>
                </div>
                <Button
                    size="sm"
                    variant="ghost"
                    className="shrink-0 ml-2"
                    onClick={handlePlayPause}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : isPlaying ? (
                        <Square className="w-4 h-4" />
                    ) : (
                        <Play className="w-4 h-4" />
                    )}
                </Button>
            </div>
            <p className="text-xs text-muted-foreground">{description}</p>
        </div>
    );
};

export default function LiveSessionComponent({ onClose, onSessionEnd }) {
    const { token } = useAuth();
    const [phase, setPhase] = useState('setup'); // setup, active, summary
    const [config, setConfig] = useState({
        mode: 'free',
        surah: null,
        ayat_start: 1,
        ayat_end: null,
        interrupt_style: 'gentle',
        focus: 'both',
        user_level: 'beginner',
        show_verses: true // Option to show/hide verses during session
    });
    const [sessionId, setSessionId] = useState(null);
    const [welcomeMessage, setWelcomeMessage] = useState('');
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [feedback, setFeedback] = useState([]);
    const [currentFeedback, setCurrentFeedback] = useState(null);
    const [isPlayingAudio, setIsPlayingAudio] = useState(false);
    const [sessionSummary, setSessionSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [micPermission, setMicPermission] = useState('unknown'); // unknown, granted, denied, prompt
    const [checkingMic, setCheckingMic] = useState(true);
    const [chatInput, setChatInput] = useState('');
    const [chatMessages, setChatMessages] = useState([]);
    const [isSendingChat, setIsSendingChat] = useState(false);
    const [guidedVerses, setGuidedVerses] = useState(null); // Store the verses for guided mode
    const [loadingVerses, setLoadingVerses] = useState(false);
    
    const mediaRecorderRef = useRef(null);
    const audioChunksRef = useRef([]);
    const audioRef = useRef(null);
    const userAudioRef = useRef(null); // Separate ref for user recitation playback
    const streamRef = useRef(null);

    // Check microphone permission on mount - use direct access test for reliability
    useEffect(() => {
        const checkMicPermission = async () => {
            setCheckingMic(true);
            
            // Check if getUserMedia is available
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                setMicPermission('unsupported');
                setCheckingMic(false);
                return;
            }

            // Try direct microphone access to verify permission (more reliable than Permissions API)
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
                // Permission granted - stop the stream immediately
                stream.getTracks().forEach(track => track.stop());
                setMicPermission('granted');
                console.log('Microphone permission verified: granted');
            } catch (error) {
                console.log('Microphone check error:', error.name, error.message);
                
                if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                    // Check if it's a "user gesture required" situation vs actual denial
                    // Try Permissions API as fallback to see the actual state
                    if (navigator.permissions && navigator.permissions.query) {
                        try {
                            const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
                            console.log('Permissions API state:', permissionStatus.state);
                            if (permissionStatus.state === 'granted') {
                                // Permission is granted but getUserMedia failed - might need user interaction
                                setMicPermission('granted');
                            } else if (permissionStatus.state === 'denied') {
                                setMicPermission('denied');
                            } else {
                                setMicPermission('prompt');
                            }
                            // Listen for permission changes
                            permissionStatus.onchange = () => {
                                setMicPermission(permissionStatus.state);
                            };
                        } catch (permErr) {
                            // Permissions API failed, assume we need to prompt
                            setMicPermission('prompt');
                        }
                    } else {
                        setMicPermission('prompt');
                    }
                } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                    setMicPermission('not_found');
                } else {
                    // Other errors - allow user to try
                    setMicPermission('prompt');
                }
            }
            
            setCheckingMic(false);
        };

        checkMicPermission();
    }, []);

    // Function to request microphone permission
    const requestMicPermission = async () => {
        setCheckingMic(true);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Permission granted - stop the stream immediately
            stream.getTracks().forEach(track => track.stop());
            setMicPermission('granted');
            toast.success('Microphone access granted!');
        } catch (error) {
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                setMicPermission('denied');
                toast.error('Microphone access denied', {
                    description: 'Click the lock icon in your address bar to enable microphone access.',
                    duration: 8000
                });
            } else if (error.name === 'NotFoundError') {
                setMicPermission('not_found');
                toast.error('No microphone found', {
                    description: 'Please connect a microphone to your device.'
                });
            } else {
                setMicPermission('error');
                toast.error('Microphone error', {
                    description: error.message || 'Unable to access microphone'
                });
            }
        }
        setCheckingMic(false);
    };

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
            if (audioRef.current) {
                audioRef.current.pause();
            }
            if (userAudioRef.current) {
                userAudioRef.current.pause();
            }
        };
    }, []);

    // Fetch Quran verses when surah is selected in guided mode
    const fetchGuidedVerses = useCallback(async (surahName, ayatStart, ayatEnd) => {
        if (!surahName) return;
        
        setLoadingVerses(true);
        try {
            // Map surah name to number (simplified - using common surahs)
            const surahMap = {
                'al-fatiha': 1,
                'al-baqarah': 2,
                'ya-sin': 36,
                'ar-rahman': 55,
                'al-mulk': 67,
                'al-kawthar': 108,
                'al-ikhlas': 112,
                'al-falaq': 113,
                'an-nas': 114,
                'al-asr': 103
            };
            
            const surahNum = surahMap[surahName] || 1;
            const startAyat = ayatStart || 1;
            const endAyat = ayatEnd || startAyat + 4; // Default to 5 verses if no end specified
            
            // Fetch from Quran API
            const response = await axios.get(
                `https://api.alquran.cloud/v1/surah/${surahNum}/editions/quran-uthmani,en.asad`
            );
            
            if (response.data.status === 'OK') {
                const arabicEdition = response.data.data.find(e => e.edition.identifier === 'quran-uthmani');
                const englishEdition = response.data.data.find(e => e.edition.identifier === 'en.asad');
                
                const selectedVerses = [];
                const maxAyat = Math.min(endAyat, arabicEdition.ayahs.length);
                
                for (let i = startAyat - 1; i < maxAyat; i++) {
                    selectedVerses.push({
                        number: arabicEdition.ayahs[i].numberInSurah,
                        arabic: arabicEdition.ayahs[i].text,
                        english: englishEdition?.ayahs[i]?.text || ''
                    });
                }
                
                setGuidedVerses({
                    surahName: arabicEdition.englishName,
                    surahNameArabic: arabicEdition.name,
                    verses: selectedVerses
                });
            }
        } catch (error) {
            console.error('Error fetching verses:', error);
            // Fallback - don't block the session
            setGuidedVerses(null);
        } finally {
            setLoadingVerses(false);
        }
    }, []);

    // Fetch verses when surah selection changes in guided mode
    useEffect(() => {
        if (config.mode === 'guided' && config.surah) {
            fetchGuidedVerses(config.surah, config.ayat_start, config.ayat_end);
        } else {
            setGuidedVerses(null);
        }
    }, [config.mode, config.surah, config.ayat_start, config.ayat_end, fetchGuidedVerses]);

    const startSession = async () => {
        setLoading(true);
        try {
            const response = await axios.post(`${API}/ustadh/live-session/start`, {
                config: config
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setSessionId(response.data.session_id);
            setWelcomeMessage(response.data.welcome_message);
            setConfig(prev => ({ ...prev, user_level: response.data.user_level }));
            setPhase('active');
            
            toast.success('Session started!', {
                description: 'Begin reciting when ready'
            });
        } catch (error) {
            toast.error('Failed to start session', {
                description: error.response?.data?.detail || 'Please try again'
            });
        } finally {
            setLoading(false);
        }
    };

    const startRecording = async () => {
        // First check if mediaDevices API is available
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            toast.error('Microphone not supported', {
                description: 'Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari.',
                duration: 8000
            });
            return;
        }

        try {
            // Check current permission status first (if Permissions API is available)
            if (navigator.permissions && navigator.permissions.query) {
                try {
                    const permissionStatus = await navigator.permissions.query({ name: 'microphone' });
                    if (permissionStatus.state === 'denied') {
                        toast.error('Microphone blocked', {
                            description: 'Microphone access was previously denied. Please click the lock/site settings icon in your browser address bar and allow microphone access, then refresh the page.',
                            duration: 10000
                        });
                        return;
                    }
                } catch (permErr) {
                    // Permissions API may not be fully supported, continue with getUserMedia
                    console.log('Permissions API not fully supported, continuing...');
                }
            }

            const stream = await navigator.mediaDevices.getUserMedia({ 
                audio: {
                    echoCancellation: true,
                    noiseSuppression: true,
                    sampleRate: 44100
                }
            });
            streamRef.current = stream;
            
            // Check for supported mimeTypes
            let mimeType = 'audio/webm;codecs=opus';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = 'audio/webm';
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    mimeType = 'audio/mp4';
                    if (!MediaRecorder.isTypeSupported(mimeType)) {
                        mimeType = ''; // Let browser choose default
                    }
                }
            }
            
            const recorderOptions = mimeType ? { mimeType } : {};
            mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);
            audioChunksRef.current = [];

            // Record full recitation, feedback after
            mediaRecorderRef.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorderRef.current.onstop = async () => {
                const audioBlob = new Blob(audioChunksRef.current, { type: mimeType || 'audio/webm' });
                await processRecitation(audioBlob);
            };

            toast.success('Recording started', {
                description: 'Recite clearly, feedback will come after you finish'
            });

            mediaRecorderRef.current.start(1000); // Collect data every second
            setIsRecording(true);
            
        } catch (error) {
            console.error('Microphone error:', error);
            
            // Provide specific error messages based on the error type
            if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
                toast.error('Microphone permission needed', {
                    description: 'Please allow microphone access when prompted. If no prompt appeared, click the lock icon in your browser address bar and enable microphone.',
                    duration: 10000
                });
            } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
                toast.error('No microphone found', {
                    description: 'Please connect a microphone to your device and try again.',
                    duration: 8000
                });
            } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
                toast.error('Microphone in use', {
                    description: 'Your microphone may be in use by another application. Please close other apps using the mic and try again.',
                    duration: 8000
                });
            } else if (error.name === 'OverconstrainedError') {
                // Try again with simpler constraints
                try {
                    const simpleStream = await navigator.mediaDevices.getUserMedia({ audio: true });
                    streamRef.current = simpleStream;
                    mediaRecorderRef.current = new MediaRecorder(simpleStream);
                    audioChunksRef.current = [];
                    mediaRecorderRef.current.ondataavailable = (event) => {
                        if (event.data.size > 0) audioChunksRef.current.push(event.data);
                    };
                    mediaRecorderRef.current.onstop = async () => {
                        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
                        await processRecitation(audioBlob);
                    };
                    mediaRecorderRef.current.start(1000);
                    setIsRecording(true);
                    
                    toast.success('Recording started', {
                        description: 'Recite clearly, feedback will play after you finish'
                    });
                    return;
                } catch (retryError) {
                    toast.error('Microphone error', {
                        description: 'Unable to access microphone with current settings.',
                        duration: 8000
                    });
                }
            } else {
                toast.error('Microphone error', {
                    description: `Unable to access microphone: ${error.message || 'Unknown error'}`,
                    duration: 8000
                });
            }
        }
    };

    const stopRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            if (mediaRecorderRef.current.state === 'recording') {
                mediaRecorderRef.current.stop();
            }
            setIsRecording(false);
            
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
            }
        }
    };

    const processRecitation = async (audioBlob) => {
        setIsProcessing(true);
        
        // Create a URL for the user's audio so they can play it back
        const userAudioUrl = URL.createObjectURL(audioBlob);
        
        const formData = new FormData();
        formData.append('audio_file', audioBlob, 'recitation.webm');

        try {
            const response = await axios.post(
                `${API}/ustadh/live-session/${sessionId}/recite`,
                formData,
                {
                    headers: { 
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data'
                    },
                    timeout: 180000 // 3 minute timeout for longer recitations
                }
            );

            const newFeedback = {
                id: Date.now(),
                userAudioUrl, // Store the user's audio URL for playback
                ...response.data
            };
            
            // Add to feedback array
            setFeedback(prev => [...prev, newFeedback]);

            // Auto-play audio feedback after recitation
            if (response.data.audio_feedback) {
                playAudioFeedback(response.data.audio_feedback);
            }

        } catch (error) {
            // Clean up the audio URL on error
            URL.revokeObjectURL(userAudioUrl);
            
            // Provide more specific error messages
            let errorMessage = 'Please try again';
            if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
                errorMessage = 'Processing took too long. Try a shorter recitation.';
            } else if (error.response?.data?.detail) {
                errorMessage = error.response.data.detail;
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            toast.error('Failed to process recitation', {
                description: errorMessage
            });
            console.error('Recitation processing error:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    const playAudioFeedback = (audioUrl) => {
        // Stop any currently playing feedback audio
        if (audioRef.current) {
            audioRef.current.pause();
        }
        // Also stop user recitation audio to avoid overlap
        if (userAudioRef.current) {
            userAudioRef.current.pause();
        }
        
        const audio = new Audio(audioUrl);
        audioRef.current = audio;
        
        audio.onplay = () => setIsPlayingAudio(true);
        audio.onended = () => setIsPlayingAudio(false);
        audio.onerror = () => {
            setIsPlayingAudio(false);
            toast.error('Audio playback error');
        };
        
        audio.play();
    };

    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current.currentTime = 0;
            setIsPlayingAudio(false);
        }
    };

    // Play user's recitation audio - stops any previous playback first
    const playUserRecitation = (audioUrl) => {
        // Stop any currently playing user audio
        if (userAudioRef.current) {
            userAudioRef.current.pause();
            userAudioRef.current.currentTime = 0;
        }
        // Also stop ustadh feedback audio to avoid overlap
        if (audioRef.current) {
            audioRef.current.pause();
        }
        
        const audio = new Audio(audioUrl);
        userAudioRef.current = audio;
        audio.play();
    };

    const sendChatMessage = async (e) => {
        e.preventDefault();
        if (!chatInput.trim() || isSendingChat) return;

        const userMessage = chatInput.trim();
        setChatInput('');
        setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
        setIsSendingChat(true);

        try {
            const response = await axios.post(
                `${API}/ustadh/live-session/${sessionId}/chat`,
                { message: userMessage },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            
            setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
        } catch (error) {
            toast.error('Failed to send message');
            setChatMessages(prev => prev.slice(0, -1)); // Remove the user message on error
        } finally {
            setIsSendingChat(false);
        }
    };

    const endSession = async () => {
        setLoading(true);
        try {
            const response = await axios.post(
                `${API}/ustadh/live-session/${sessionId}/end`,
                {},
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );
            
            setSessionSummary(response.data);
            setPhase('summary');
            
            if (onSessionEnd) {
                onSessionEnd(response.data);
            }
        } catch (error) {
            toast.error('Failed to end session', {
                description: error.response?.data?.detail || 'Please try again'
            });
        } finally {
            setLoading(false);
        }
    };

    const selectedSurah = SURAHS.find(s => s.value === config.surah);

    // Setup Phase UI
    if (phase === 'setup') {
        return (
            <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full max-w-2xl my-4"
                >
                    <Card className="bg-card border-secondary/20 max-h-[90vh] overflow-auto">
                        <CardHeader className="text-center pb-2 px-4 sm:px-6">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                <GraduationCap className="w-6 h-6 sm:w-8 sm:h-8 text-secondary" />
                            </div>
                            <CardTitle className="font-heading text-xl sm:text-2xl">Start Live Session</CardTitle>
                            <CardDescription className="text-sm">
                                Configure your recitation practice
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4 sm:space-y-6 px-4 sm:px-6">
                            {/* Session Mode */}
                            <div className="space-y-2 sm:space-y-3">
                                <Label className="text-sm sm:text-base font-medium">Session Type</Label>
                                <RadioGroup
                                    value={config.mode}
                                    onValueChange={(value) => setConfig(prev => ({ ...prev, mode: value }))}
                                    className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4"
                                >
                                    <div className={`flex items-center space-x-3 p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-colors ${config.mode === 'free' ? 'border-secondary bg-secondary/5' : 'border-border hover:border-secondary/50'}`}>
                                        <RadioGroupItem value="free" id="free" />
                                        <Label htmlFor="free" className="cursor-pointer flex-1">
                                            <div className="font-medium text-sm sm:text-base">Free Recitation</div>
                                            <div className="text-xs text-muted-foreground">I'll identify what you recite</div>
                                        </Label>
                                    </div>
                                    <div className={`flex items-center space-x-3 p-3 sm:p-4 rounded-lg border-2 cursor-pointer transition-colors ${config.mode === 'guided' ? 'border-secondary bg-secondary/5' : 'border-border hover:border-secondary/50'}`}>
                                        <RadioGroupItem value="guided" id="guided" />
                                        <Label htmlFor="guided" className="cursor-pointer flex-1">
                                            <div className="font-medium text-sm sm:text-base">Guided Practice</div>
                                            <div className="text-xs text-muted-foreground">Practice specific Surah/Ayat</div>
                                        </Label>
                                    </div>
                                </RadioGroup>
                            </div>

                            {/* Surah Selection (for guided mode) */}
                            <AnimatePresence>
                                {config.mode === 'guided' && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="space-y-3 overflow-hidden"
                                    >
                                        <Label className="text-base font-medium">Select Surah</Label>
                                        <Select
                                            value={config.surah || ''}
                                            onValueChange={(value) => {
                                                const surah = SURAHS.find(s => s.value === value);
                                                setConfig(prev => ({ 
                                                    ...prev, 
                                                    surah: value,
                                                    ayat_end: surah?.ayat || null
                                                }));
                                            }}
                                        >
                                            <SelectTrigger data-testid="surah-select">
                                                <SelectValue placeholder="Choose a Surah" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {SURAHS.map(surah => (
                                                    <SelectItem key={surah.value} value={surah.value}>
                                                        {surah.label} ({surah.ayat} ayat)
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        
                                        {selectedSurah && (
                                            <div className="grid grid-cols-2 gap-4">
                                                <div>
                                                    <Label className="text-sm">From Ayah</Label>
                                                    <Select
                                                        value={String(config.ayat_start)}
                                                        onValueChange={(v) => setConfig(prev => ({ ...prev, ayat_start: parseInt(v) }))}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {Array.from({ length: selectedSurah.ayat }, (_, i) => (
                                                                <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div>
                                                    <Label className="text-sm">To Ayah</Label>
                                                    <Select
                                                        value={String(config.ayat_end || selectedSurah.ayat)}
                                                        onValueChange={(v) => setConfig(prev => ({ ...prev, ayat_end: parseInt(v) }))}
                                                    >
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {Array.from({ length: selectedSurah.ayat }, (_, i) => (
                                                                <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                        )}
                                        
                                        {/* Preview of selected verses */}
                                        {selectedSurah && guidedVerses && (
                                            <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border">
                                                <div className="flex items-center gap-2 mb-2">
                                                    <BookOpen className="w-4 h-4 text-secondary" />
                                                    <span className="text-xs font-medium text-secondary">Verses to Practice</span>
                                                </div>
                                                <div className="space-y-3 max-h-48 overflow-auto">
                                                    {guidedVerses.verses.map((verse, idx) => (
                                                        <div key={idx} className="space-y-1">
                                                            <p className="font-arabic text-lg text-right leading-loose" dir="rtl">
                                                                {verse.arabic} <span className="text-secondary text-sm">﴿{verse.number}﴾</span>
                                                            </p>
                                                            {verse.english && (
                                                                <p className="text-xs text-muted-foreground">
                                                                    {verse.english}
                                                                </p>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                        {selectedSurah && loadingVerses && (
                                            <div className="mt-4 p-3 bg-muted/50 rounded-lg border border-border flex items-center justify-center">
                                                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                                <span className="text-sm text-muted-foreground">Loading verses...</span>
                                            </div>
                                        )}
                                        
                                        {/* Option to show/hide verses during session */}
                                        {selectedSurah && guidedVerses && (
                                            <div className="mt-3 flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                                                <div className="flex items-center gap-2">
                                                    <Eye className="w-4 h-4 text-muted-foreground" />
                                                    <span className="text-sm">Show verses during session</span>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => setConfig(prev => ({ ...prev, show_verses: !prev.show_verses }))}
                                                    className={`relative w-12 h-6 rounded-full transition-colors ${config.show_verses ? 'bg-secondary' : 'bg-muted'}`}
                                                >
                                                    <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-all duration-200 ${config.show_verses ? 'left-[calc(100%-22px)] bg-white' : 'left-0.5 bg-white/80'}`} />
                                                </button>
                                            </div>
                                        )}
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Reference Reciters - Listen to Master Reciters */}
                            <AnimatePresence>
                                {config.mode === 'guided' && selectedSurah && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: 'auto' }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="space-y-3 overflow-hidden"
                                    >
                                        <Label className="text-sm sm:text-base font-medium flex items-center gap-2">
                                            <Volume2 className="w-4 h-4 text-secondary" />
                                            Listen to Master Reciters
                                        </Label>
                                        <p className="text-xs text-muted-foreground">
                                            Listen to how renowned reciters perform this Surah before practicing
                                        </p>
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                            <ReferenceReciterButton
                                                name="Sheikh Mahmoud Khalil Al-Husary"
                                                style="Clear Murattal"
                                                description="Known for precise tajweed and deliberate pace"
                                                surah={config.surah}
                                                reciterId="ar.husary"
                                            />
                                            <ReferenceReciterButton
                                                name="Sheikh Muhammad Siddiq al-Minshawi"
                                                style="Tajweed Master"
                                                description="Renowned for melodious recitation and perfect tajweed"
                                                surah={config.surah}
                                                reciterId="ar.minshawi"
                                            />
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>

                            {/* Focus Area */}
                            <div className="space-y-2 sm:space-y-3">
                                <Label className="text-sm sm:text-base font-medium">Focus Area</Label>
                                <RadioGroup
                                    value={config.focus}
                                    onValueChange={(value) => setConfig(prev => ({ ...prev, focus: value }))}
                                    className="flex flex-wrap gap-2 sm:gap-3"
                                >
                                    {[
                                        { value: 'pronunciation', label: 'Pronunciation' },
                                        { value: 'tajweed', label: 'Tajweed' },
                                        { value: 'both', label: 'Both' }
                                    ].map(option => (
                                        <div 
                                            key={option.value}
                                            className={`flex items-center space-x-2 px-3 sm:px-4 py-2 rounded-full border-2 cursor-pointer transition-colors ${config.focus === option.value ? 'border-secondary bg-secondary/5' : 'border-border hover:border-secondary/50'}`}
                                        >
                                            <RadioGroupItem value={option.value} id={option.value} />
                                            <Label htmlFor={option.value} className="cursor-pointer text-xs sm:text-sm">
                                                {option.label}
                                            </Label>
                                        </div>
                                    ))}
                                </RadioGroup>
                            </div>

                            {/* Microphone Permission Check */}
                            <div className="space-y-3">
                                <Label className="text-base font-medium flex items-center gap-2">
                                    <Mic className="w-4 h-4" />
                                    Microphone Access
                                </Label>
                                <div className={`p-4 rounded-lg border-2 ${
                                    micPermission === 'granted' 
                                        ? 'border-primary/50 bg-primary/5' 
                                        : micPermission === 'denied' 
                                            ? 'border-destructive/50 bg-destructive/5'
                                            : 'border-border bg-muted/30'
                                }`}>
                                    {checkingMic ? (
                                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Checking microphone access...
                                        </div>
                                    ) : micPermission === 'granted' ? (
                                        <div className="flex items-center gap-2 text-sm text-primary">
                                            <CheckCircle2 className="w-4 h-4" />
                                            Microphone access granted - ready to record!
                                        </div>
                                    ) : micPermission === 'denied' ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-sm text-destructive">
                                                <XCircle className="w-4 h-4" />
                                                Microphone access blocked
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                To enable: Click the <strong>lock/tune icon</strong> in your browser address bar for <strong>{window.location.hostname}</strong>, 
                                                set Microphone to "Allow", then <strong>refresh this page</strong>.
                                            </p>
                                        </div>
                                    ) : micPermission === 'unsupported' ? (
                                        <div className="flex items-center gap-2 text-sm text-destructive">
                                            <AlertCircle className="w-4 h-4" />
                                            Your browser doesn't support microphone access
                                        </div>
                                    ) : micPermission === 'not_found' ? (
                                        <div className="space-y-2">
                                            <div className="flex items-center gap-2 text-sm text-yellow-500">
                                                <AlertCircle className="w-4 h-4" />
                                                No microphone detected
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Please connect a microphone to your device. If you have one connected, 
                                                check that it's selected as the default input device in your system settings.
                                            </p>
                                        </div>
                                    ) : (
                                        <div className="space-y-3">
                                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <AlertCircle className="w-4 h-4" />
                                                Microphone permission required for live recitation
                                            </div>
                                            <p className="text-xs text-muted-foreground">
                                                Note: Permission must be granted for <strong>{window.location.hostname}</strong>
                                            </p>
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={requestMicPermission}
                                                disabled={checkingMic}
                                                className="w-full"
                                                data-testid="request-mic-permission-btn"
                                            >
                                                <Mic className="w-4 h-4 mr-2" />
                                                Allow Microphone Access
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex gap-3 pt-2 sm:pt-4">
                                <Button 
                                    variant="outline" 
                                    onClick={onClose}
                                    className="flex-1 text-sm sm:text-base"
                                >
                                    Cancel
                                </Button>
                                <Button 
                                    onClick={startSession}
                                    disabled={loading || (config.mode === 'guided' && !config.surah) || micPermission === 'denied' || micPermission === 'unsupported'}
                                    className="flex-1 bg-secondary hover:bg-secondary/90 text-sm sm:text-base"
                                >
                                    {loading ? (
                                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                                    ) : (
                                        <Play className="w-4 h-4 mr-2" />
                                    )}
                                    Begin
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        );
    }

    // Active Session UI
    if (phase === 'active') {
        return (
            <div className="fixed inset-0 bg-background z-50 flex flex-col">
                {/* Header */}
                <div className="bg-card border-b border-border px-4 py-3">
                    <div className="max-w-4xl mx-auto flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-secondary/10">
                                <GraduationCap className="w-5 h-5 text-secondary" />
                            </div>
                            <div>
                                <h2 className="font-heading font-semibold flex items-center gap-2">
                                    Live Session
                                </h2>
                                <p className="text-xs text-muted-foreground">
                                    {config.mode === 'guided' && config.surah 
                                        ? `${SURAHS.find(s => s.value === config.surah)?.label}`
                                        : 'Free Recitation Mode'
                                    }
                                </p>
                            </div>
                        </div>
                        <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={endSession}
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Square className="w-4 h-4 mr-1" />}
                            End Session
                        </Button>
                    </div>
                </div>

                {/* Main Content */}
                <div className="flex-1 overflow-auto p-4">
                    <div className="max-w-4xl mx-auto space-y-4">
                        
                        {/* Guided Mode: Show verses to recite (if enabled) */}
                        {config.mode === 'guided' && guidedVerses && config.show_verses && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-card border border-secondary/30 rounded-xl p-4 mb-4"
                            >
                                <div className="flex items-center gap-2 mb-3">
                                    <BookOpen className="w-5 h-5 text-secondary" />
                                    <h3 className="font-heading font-semibold text-secondary">
                                        {guidedVerses.surahName} - {guidedVerses.surahNameArabic}
                                    </h3>
                                </div>
                                <div className="space-y-4 max-h-64 overflow-auto pr-2">
                                    {guidedVerses.verses.map((verse, idx) => (
                                        <div key={idx} className="pb-3 border-b border-border last:border-0 last:pb-0">
                                            <p className="font-arabic text-xl sm:text-2xl text-right leading-loose mb-2" dir="rtl">
                                                {verse.arabic} <span className="text-secondary">﴿{verse.number}﴾</span>
                                            </p>
                                            {verse.english && (
                                                <p className="text-sm text-muted-foreground">
                                                    {verse.english}
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <p className="text-xs text-muted-foreground mt-3 text-center">
                                    Recite the verses above. I will provide feedback on your tajweed and pronunciation.
                                </p>
                            </motion.div>
                        )}
                        
                        {/* Welcome Message */}
                        {welcomeMessage && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="flex gap-3"
                            >
                                <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                    <GraduationCap className="w-4 h-4 text-secondary" />
                                </div>
                                <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3 max-w-[85%]">
                                    <p className="text-sm">{welcomeMessage}</p>
                                </div>
                            </motion.div>
                        )}

                        {/* All Feedback as Chat Flow */}
                        {feedback.map((item, index) => (
                            <motion.div
                                key={item.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="space-y-3"
                            >
                                {/* User's recitation (voice note style) */}
                                <div className="flex gap-3 justify-end">
                                    <div className="bg-secondary text-secondary-foreground rounded-2xl rounded-tr-none px-4 py-3 max-w-[85%]">
                                        {/* Play user's audio */}
                                        {item.userAudioUrl && (
                                            <div className="flex items-center gap-2 mb-2 pb-2 border-b border-secondary-foreground/20">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 px-2 text-secondary-foreground hover:bg-secondary-foreground/10"
                                                    onClick={() => playUserRecitation(item.userAudioUrl)}
                                                    data-testid={`play-user-audio-${index}`}
                                                >
                                                    <Play className="w-4 h-4 mr-1" />
                                                    <span className="text-xs">Play my recitation</span>
                                                </Button>
                                            </div>
                                        )}
                                        <p className="text-lg font-arabic text-right" dir="rtl">
                                            {item.transcribed_text}
                                        </p>
                                        {item.identified_surah && (
                                            <p className="text-xs opacity-80 mt-1">
                                                {item.identified_surah} {item.identified_ayat && `- Ayat ${item.identified_ayat}`}
                                            </p>
                                        )}
                                    </div>
                                    <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                                        <Mic className="w-4 h-4" />
                                    </div>
                                </div>

                                {/* Ustadh's response */}
                                <div className="flex gap-3">
                                    <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                        <GraduationCap className="w-4 h-4 text-secondary" />
                                    </div>
                                    <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3 max-w-[85%] space-y-3">
                                        {/* Praise first */}
                                        {item.praise && (
                                            <div className="flex items-start gap-2">
                                                <CheckCircle2 className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                                                <p className="text-sm"><MarkdownText>{item.praise}</MarkdownText></p>
                                            </div>
                                        )}
                                        
                                        {/* Corrections if any */}
                                        {item.mistakes?.length > 0 && (
                                            <div className="space-y-2 pt-2 border-t border-border">
                                                <p className="text-xs text-muted-foreground">Areas to improve:</p>
                                                {item.mistakes.map((mistake, i) => (
                                                    <div key={i} className="flex items-start gap-2 text-sm">
                                                        <AlertCircle className="w-3 h-3 text-yellow-500 shrink-0 mt-1" />
                                                        <span>
                                                            <strong>{mistake.word}</strong> - <MarkdownText>{mistake.correction}</MarkdownText>
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                        
                                        {/* Reciter Tip - Reference to Master Reciters */}
                                        {item.reciter_tip && (
                                            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 mt-2">
                                                <p className="text-xs text-emerald-400 font-medium flex items-center gap-1">
                                                    <span>🎙️</span> Master Reciter Tip
                                                </p>
                                                <p className="text-sm text-emerald-300/90 mt-1">
                                                    <MarkdownText>{item.reciter_tip}</MarkdownText>
                                                </p>
                                            </div>
                                        )}
                                        
                                        {/* Next steps */}
                                        {item.next_steps && (
                                            <p className="text-xs text-muted-foreground pt-2 border-t border-border">
                                                <MarkdownText>{item.next_steps}</MarkdownText>
                                            </p>
                                        )}
                                        
                                        {/* Replay Ustadh's audio */}
                                        {item.audio_feedback && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => playAudioFeedback(item.audio_feedback)}
                                                className="h-7 text-xs mt-1"
                                                data-testid={`replay-feedback-${index}`}
                                            >
                                                <Volume2 className="w-3 h-3 mr-1" />
                                                Replay audio
                                            </Button>
                                        )}
                                    </div>
                                </div>
                            </motion.div>
                        ))}

                        {/* Processing Indicator */}
                        {isProcessing && (
                            <div className="flex gap-3">
                                <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                    <GraduationCap className="w-4 h-4 text-secondary" />
                                </div>
                                <div className="bg-muted rounded-2xl rounded-tl-none px-4 py-3">
                                    <div className="flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin text-secondary" />
                                        <span className="text-sm text-muted-foreground">Listening to your recitation...</span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Chat Messages */}
                        {chatMessages.length > 0 && (
                            <div className="space-y-3 pt-4 border-t border-border">
                                <Label className="text-xs text-muted-foreground flex items-center gap-1">
                                    <MessageSquare className="w-3 h-3" />
                                    Questions about your recitation:
                                </Label>
                                {chatMessages.map((msg, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className={`flex gap-2 ${msg.role === 'user' ? 'justify-end' : ''}`}
                                    >
                                        {msg.role === 'assistant' && (
                                            <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                                <GraduationCap className="w-4 h-4 text-secondary" />
                                            </div>
                                        )}
                                        <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm ${
                                            msg.role === 'user' 
                                                ? 'bg-secondary text-secondary-foreground rounded-tr-none' 
                                                : 'bg-muted rounded-tl-none'
                                        }`}>
                                            {msg.role === 'assistant' ? (
                                                <MarkdownText>{msg.content}</MarkdownText>
                                            ) : (
                                                msg.content
                                            )}
                                        </div>
                                        {msg.role === 'user' && (
                                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center shrink-0">
                                                <MessageSquare className="w-4 h-4" />
                                            </div>
                                        )}
                                    </motion.div>
                                ))}
                                {isSendingChat && (
                                    <div className="flex gap-2">
                                        <div className="w-8 h-8 rounded-full bg-secondary/10 flex items-center justify-center shrink-0">
                                            <GraduationCap className="w-4 h-4 text-secondary" />
                                        </div>
                                        <div className="bg-muted px-4 py-3 rounded-2xl rounded-tl-none">
                                            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Recording Controls + Chat Input */}
                <div className="bg-card border-t border-border p-4">
                    <div className="max-w-4xl mx-auto">
                        {/* Mic and Chat Row */}
                        <div className="flex items-center gap-4">
                            {/* Mic Button */}
                            <motion.button
                                onClick={isRecording ? stopRecording : startRecording}
                                disabled={isProcessing}
                                className={`w-16 h-16 rounded-full flex items-center justify-center transition-all shrink-0 ${
                                    isRecording 
                                        ? 'bg-destructive text-destructive-foreground animate-pulse' 
                                        : 'bg-secondary text-secondary-foreground hover:bg-secondary/90'
                                } ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
                                whileTap={{ scale: 0.95 }}
                                data-testid="live-session-record-btn"
                            >
                                {isRecording ? (
                                    <MicOff className="w-7 h-7" />
                                ) : (
                                    <Mic className="w-7 h-7" />
                                )}
                            </motion.button>

                            {/* Chat Input */}
                            <form onSubmit={sendChatMessage} className="flex-1 flex gap-2">
                                <Input
                                    value={chatInput}
                                    onChange={(e) => setChatInput(e.target.value)}
                                    placeholder="Ask about your recitation..."
                                    disabled={isSendingChat || isRecording}
                                    className="flex-1"
                                    data-testid="live-session-chat-input"
                                />
                                <Button
                                    type="submit"
                                    disabled={!chatInput.trim() || isSendingChat || isRecording}
                                    size="icon"
                                    className="bg-secondary hover:bg-secondary/90"
                                    data-testid="live-session-chat-send"
                                >
                                    {isSendingChat ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Send className="w-4 h-4" />
                                    )}
                                </Button>
                            </form>
                        </div>

                        {/* Status Text */}
                        <p className="text-xs text-muted-foreground text-center mt-3">
                            {isRecording 
                                ? 'Recording... Click mic to stop and get feedback'
                                : isProcessing 
                                    ? 'Processing your recitation...'
                                    : 'Click the microphone to recite, or type a question about your recitation'
                            }
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Summary Phase UI
    if (phase === 'summary') {
        return (
            <div className="fixed inset-0 bg-background/95 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-auto">
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="w-full max-w-2xl my-8"
                >
                    <Card className="bg-card border-secondary/20">
                        <CardHeader className="text-center pb-2">
                            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-8 h-8 text-primary" />
                            </div>
                            <CardTitle className="font-heading text-2xl">Session Complete!</CardTitle>
                            <CardDescription>
                                MashaAllah! Here's your session summary
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <div className="text-center p-4 rounded-lg bg-muted/50">
                                    <div className="text-2xl font-bold text-primary">
                                        {sessionSummary?.duration_minutes || 0}
                                    </div>
                                    <div className="text-xs text-muted-foreground">Minutes</div>
                                </div>
                                <div className="text-center p-4 rounded-lg bg-muted/50">
                                    <div className="text-2xl font-bold text-secondary">
                                        {sessionSummary?.total_segments || 0}
                                    </div>
                                    <div className="text-xs text-muted-foreground">Recitations</div>
                                </div>
                                <div className="text-center p-4 rounded-lg bg-muted/50">
                                    <div className="text-2xl font-bold text-yellow-500">
                                        {sessionSummary?.total_corrections || 0}
                                    </div>
                                    <div className="text-xs text-muted-foreground">Corrections</div>
                                </div>
                            </div>

                            {/* Summary Text */}
                            {sessionSummary?.summary && (
                                <div className="p-4 rounded-lg bg-secondary/5 border border-secondary/20">
                                    <div className="prose prose-sm dark:prose-invert max-w-none text-sm">
                                        <ReactMarkdown
                                            components={{
                                                p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                                                strong: ({children}) => <strong className="font-semibold">{children}</strong>,
                                                em: ({children}) => <em className="italic">{children}</em>,
                                                ul: ({children}) => <ul className="list-disc list-inside mb-2">{children}</ul>,
                                                ol: ({children}) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
                                                li: ({children}) => <li className="mb-1">{children}</li>,
                                                h1: ({children}) => <h3 className="font-semibold text-base mb-2">{children}</h3>,
                                                h2: ({children}) => <h4 className="font-semibold text-base mb-2">{children}</h4>,
                                                h3: ({children}) => <h5 className="font-semibold text-sm mb-1">{children}</h5>,
                                            }}
                                        >
                                            {sessionSummary.summary}
                                        </ReactMarkdown>
                                    </div>
                                </div>
                            )}

                            {/* Close Button */}
                            <Button 
                                onClick={onClose}
                                className="w-full bg-secondary hover:bg-secondary/90"
                            >
                                Close Session
                            </Button>
                        </CardContent>
                    </Card>
                </motion.div>
            </div>
        );
    }

    return null;
}
